import BenchTips from './BenchTips';
import { useState, type ReactNode } from "react";
import {
  ArrowRight,
  Check,
  Clock3,
  FileText,
  LockKeyhole,
  Play,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import type { DemoState } from "../domain/demo";
import { log } from "../domain/demo";
import {
  enterpriseSystems,
  extraConnectors,
  materialAvailable,
  materials,
  parts,
  purchaseStep,
  readiness,
  shifts,
  type Purchase,
} from "../domain/operations";
import { instruments, packs } from "../data/catalog";
import { KNOWLEDGE_HUB_CATEGORIES } from "../domain/knowledgeTypes";
import {
  evidenceChecklist,
  evidenceEnvelope,
  recordIndex,
} from "../domain/records";

type Props = {
  s: DemoState;
  mutate: (fn: (d: DemoState) => void, message: string) => void;
  go: (p: any) => void;
};
const Tag = ({
  children,
  tone = "",
}: {
  children: ReactNode;
  tone?: string;
}) => <span className={"badge " + tone}>{children}</span>;
const Btn = ({
  children,
  onClick,
  disabled = false,
  primary = false,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) => (
  <button
    className={"button " + (primary ? "primary" : "")}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);
const Card = ({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) => (
  <section className="panel">
    <div className="panel-head">
      <h2>{title}</h2>
      {action}
    </div>
    {children}
  </section>
);
function download(name: string, body: string) {
  const u = URL.createObjectURL(new Blob([body], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = u;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
}

export function AttentionQueue({ s, mutate, go }: Props) {
  const all = [
    {
      id: "AT-02",
      type: "Calibration",
      title: "DENS-01 · reference-fluid verification due at 15:00",
      detail: "Nadia Rahman · before evening-shift release",
      page: "schedule",
    },
    {
      id: "AT-03",
      type: "Materials",
      title: "Nitrogen reserve below overnight requirement",
      detail: "One cylinder available; two required · ELEM-01",
      page: "marketplace",
    },
    {
      id: "AT-04",
      type: "People",
      title: "Night-shift D93 authorization gap",
      detail: "Assign a qualified analyst before FLASH-01 batch",
      page: "schedule",
    },
  ].filter(a => a.id === "AT-03" ? materialAvailable(s, "MAT-N2") < 2 : a.id === "AT-04" ? !s.ops.coverage.night : true);
  return (
    <Card
      title="Other items needing attention"
      action={
        <Tag>
          {all.filter((a) => !s.ops.acknowledged.includes(a.id)).length}{" "}
          unacknowledged
        </Tag>
      }
    >
      <div className="attention-list">
        {all.map((a) => (
          <div key={a.id}>
            <span className="badge amber">{a.type}</span>
            <button className="attention-link" onClick={() => go(a.page)}>
              <strong>{a.title}</strong>
              <small>{a.detail}</small>
            </button>
            <button
              className="text-link"
              disabled={s.ops.acknowledged.includes(a.id)}
              onClick={() =>
                mutate((d) => {
                  d.ops.acknowledged.push(a.id);
                  log(d, "Attention item acknowledged", a.id, a.title);
                }, "Acknowledged; action still required.")
              }
            >
              {s.ops.acknowledged.includes(a.id)
                ? "Acknowledged"
                : "Acknowledge"}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ShiftPlanner({ s, mutate, go }: Props) {
  const r = readiness(s);
  const [view, setView] = useState("24 hours");
  const jobs = [
    {
      asset: "DIST-04",
      person: "Luis Ortega",
      cells: [
        "Northstar D86",
        "Quality review",
        s.corrected ? "Corrected / QC" : "Contained",
        s.verified ? "Available" : "Recovery gate",
        "Routine D86",
        "Routine D86",
        "Overnight QC",
        "Reserve",
      ],
    },
    {
      asset: "DIST-02",
      person: "Aisha → Nadia → Mateo",
      cells: [
        "Meridian D86",
        "Routine runs",
        "Northstar retests",
        "Retest review",
        "Evening batch",
        "Routine D86",
        "Night batch",
        "Shift QC",
      ],
    },
    {
      asset: "FLASH-01",
      person: s.ops.coverage.night ? "Owen → Jules" : "Owen · night gap",
      cells: [
        "Harbor D93",
        "QC / ignition check",
        "Customer batch",
        "Evening QC",
        "D93 batch",
        "Night handover",
        s.ops.coverage.night ? "D93 staffed" : "Staffing block",
        "QC / clean",
      ],
    },
    {
      asset: "ELEM-01",
      person: "Owen → Priya",
      cells: [
        "Reference check",
        "Customer batch",
        "Routine work",
        "Shift QC",
        "Nitrogen stock check",
        materialAvailable(s, "MAT-N2") >= 2 ? "Gas ready" : "Gas shortage",
        "Overnight batch",
        "QC / clean",
      ],
    },
  ];
  const starts =
    view === "Day"
      ? [0, 1, 2]
      : view === "Evening"
        ? [3, 4, 5]
        : view === "Night"
          ? [6, 7]
          : [0, 1, 2, 3, 4, 5, 6, 7];
  return (
    <>
      <div className="compact-banner">
        <div>
          <strong>24-hour plan · 13 Oct 07:00 → 14 Oct 07:00</strong>
          <span>
            Three shifts · people, instruments and required materials in one
            view.
          </span>
        </div>
        <div className="row">
          <Tag tone={r.people === 3 ? "success" : "amber"}>
            {r.people}/3 shifts staffed
          </Tag>
          <Tag tone={r.materials === 6 ? "success" : "amber"}>
            {r.materials}/6 materials confirmed
          </Tag>
        </div>
      </div>
      <div className="shift-cards">
        {shifts.map((sh) => (
          <Card key={sh.id} title={sh.name + " · " + sh.time}>
            <div className="panel-body">
              <strong>
                {sh.people}
                {sh.id === "night" && s.ops.coverage.night
                  ? " + " + s.ops.coverage.night
                  : ""}
              </strong>
              <p>
                {sh.methods} · {sh.demand}
              </p>
              {sh.id === "night" ? (
                <label className="form-field">
                  D93 coverage
                  <select
                    aria-label="Night shift D93 coverage"
                    value={s.ops.coverage.night || ""}
                    onChange={(e) =>
                      mutate((d) => {
                        d.ops.coverage.night = e.target.value;
                        log(
                          d,
                          "Shift coverage updated",
                          "SHIFT-NIGHT",
                          e.target.value || "D93 gap reopened",
                        );
                      }, "Shift plan updated.")
                    }
                  >
                    <option value="">Unfilled · blocks D93 batch</option>
                    <option>
                      Jules Martin · D93 authorized · night relief
                    </option>
                  </select>
                </label>
              ) : (
                <Tag tone="success">Method-qualified team</Tag>
              )}
            </div>
          </Card>
        ))}
      </div>
      <div className="toolbar">
        <div className="pills">
          {["24 hours", "Day", "Evening", "Night"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={v === view ? "selected" : ""}
            >
              {v}
            </button>
          ))}
        </div>
        <span className="muted">Local laboratory time · CDT</span>
      </div>
      <Card title="Instrument and staffing plan">
        <div className="table-scroll">
          <table className="day-plan">
            <thead>
              <tr>
                <th>Asset / coverage</th>
                {starts.map((i) => (
                  <th key={i}>
                    {
                      [
                        "07–10",
                        "10–13",
                        "13–16",
                        "16–19",
                        "19–22",
                        "22–01",
                        "01–04",
                        "04–07",
                      ][i]
                    }
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.asset}>
                  <td>
                    <strong>{j.asset}</strong>
                    <small>{j.person}</small>
                  </td>
                  {starts.map((i) => (
                    <td key={i}>
                      <button
                        className={
                          "plan-cell " +
                          (/gap|block|shortage|Contained/.test(j.cells[i])
                            ? "blocked"
                            : "")
                        }
                        onClick={() =>
                          go(
                            j.asset === "DIST-04"
                              ? "capa"
                              : j.asset === "ELEM-01"
                                ? "marketplace"
                                : "samples",
                          )
                        }
                      >
                        {j.cells[i]}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card
        title="Materials readiness · confirmation required before shift handover"
        action={
          <button className="text-link" onClick={() => go("marketplace")}>
            Request parts / materials →
          </button>
        }
      >
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Material / lot</th>
                <th>Required</th>
                <th>Available</th>
                <th>Needed for</th>
                <th>Validity</th>
                <th>Verification</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m) => {
                const available = materialAvailable(s, m.id);
                return (
                  <tr key={m.id}>
                    <td>
                      <strong>{m.name}</strong>
                      <small>
                        {m.lot} · {m.method}
                      </small>
                    </td>
                    <td>
                      {m.need} {m.unit}
                    </td>
                    <td>
                      <Tag tone={available >= m.need ? "success" : "amber"}>
                        {available} {m.unit}
                      </Tag>
                    </td>
                    <td>{m.shift}</td>
                    <td>{m.expiry}</td>
                    <td>
                      <label className="inline-check">
                        <input
                          type="checkbox"
                          aria-label={"Confirm " + m.name}
                          checked={!!s.ops.materialChecks[m.id]}
                          disabled={available < m.need}
                          onChange={(e) =>
                            mutate((d) => {
                              d.ops.materialChecks[m.id] = e.target.checked;
                              log(
                                d,
                                "Material readiness checked",
                                m.id,
                                e.target.checked
                                  ? "Stock, lot and validity verified"
                                  : "Confirmation withdrawn",
                              );
                            }, "Material verification recorded.")
                          }
                        />
                        {available < m.need
                          ? "Shortage · order required"
                          : s.ops.materialChecks[m.id]
                            ? "Confirmed"
                            : "Confirm stock & expiry"}
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="CAPA-linked work orders">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Work</th>
                <th>Owner</th>
                <th>Planned time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {s.work.map((w) => (
                <tr key={w.id}>
                  <td>
                    <button className="text-link" onClick={() => go("capa")}>
                      {w.id} · {w.title}
                    </button>
                  </td>
                  <td>{w.assignee}</td>
                  <td>{w.slot}</td>
                  <td>
                    <Tag>{w.done ? "Complete" : "Planned"}</Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!s.work.length && (
            <p className="empty">
              Approved CAPA work will populate this queue.
            </p>
          )}
        </div>
      </Card>
    </>
  );
}

export function ConnectorDirectory({ s, mutate }: Props) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const all = [...instruments, ...extraConnectors];
  const [selected, setSelected] = useState(all[0].id);
  const c = all.find((x) => x.id === selected)!;
  return (
    <>
      <div className="compact-banner">
        <div>
          <strong>
            {all.length} instrument entries · compact integration catalog
          </strong>
          <span>
            Existing adapter sources are distinguished from planned connector
            specifications.
          </span>
        </div>
        <Tag>{s.ops.connectorSelection.length} selected for this lab</Tag>
      </div>
      <div className="toolbar">
        <label className="search">
          <Search size={14} />
          <input
            aria-label="Search connectors"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type, manufacturer, method or protocol"
          />
        </label>
        <div className="pills">
          {["All", "Existing adapter", "Planned", "Selected"].map((f) => (
            <button
              key={f}
              className={filter === f ? "selected" : ""}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="directory-layout">
        <Card title="Instrument connectors">
          <div className="table-scroll compact-directory">
            <table>
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Instrument / manufacturer</th>
                  <th>Coverage</th>
                  <th>Readiness</th>
                </tr>
              </thead>
              <tbody>
                {all
                  .filter(
                    (x) =>
                      (
                        x.name +
                        " " +
                        x.maker +
                        " " +
                        x.method +
                        " " +
                        x.protocol
                      )
                        .toLowerCase()
                        .includes(q.toLowerCase()) &&
                      (filter === "All" ||
                        (filter === "Existing adapter" && x.repo) ||
                        (filter === "Planned" && x.status === "Planned") ||
                        (filter === "Selected" &&
                          s.ops.connectorSelection.includes(x.id))),
                  )
                  .map((x) => (
                    <tr
                      key={x.id}
                      className={selected === x.id ? "selected-row" : ""}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={"Select " + x.id}
                          checked={s.ops.connectorSelection.includes(x.id)}
                          onChange={(e) =>
                            mutate((d) => {
                              d.ops.connectorSelection = e.target.checked
                                ? [...d.ops.connectorSelection, x.id]
                                : d.ops.connectorSelection.filter(
                                    (id) => id !== x.id,
                                  );
                              log(
                                d,
                                "Connector selection changed",
                                x.id,
                                e.target.checked
                                  ? "Selected for assessment"
                                  : "Deselected",
                              );
                            }, "Lab connector selection updated.")
                          }
                        />
                      </td>
                      <td>
                        <button
                          className="text-link"
                          onClick={() => setSelected(x.id)}
                        >
                          {x.name}
                        </button>
                        <small>
                          {x.maker} · {x.id}
                        </small>
                      </td>
                      <td>{x.method}</td>
                      <td>
                        <Tag tone={x.repo ? "success" : ""}>
                          {x.repo ? "Adapter source" : "Planned"}
                        </Tag>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card title={c.name + " · " + c.id}>
          <div className="panel-body">
            <h3>{c.maker}</h3>
            <div className="signal-row">
              <span>Protocol</span>
              <strong>{c.protocol}</strong>
            </div>
            <p>{c.signal}</p>
            {c.repo ? (
              <a
                className="text-link"
                href={"https://github.com/Enrisk/" + c.repo}
                target="_blank"
                rel="noreferrer"
              >
                Inspect existing adapter source ↗
              </a>
            ) : (
              <p className="note">
                Selectable for planning. A production adapter has not been
                verified.
              </p>
            )}
            <p className="note">
              Connection tests replay local sample payloads. No physical device
              is contacted.
            </p>
            <Btn
              primary
              onClick={() =>
                mutate((d) => {
                  d.connectorTests[c.id] = "Schema previewed";
                  log(
                    d,
                    "Connector schema preview",
                    c.id,
                    "Synthetic sample ID, timestamp, units and method mapping shown.",
                  );
                }, "Mock schema preview recorded.")
              }
            >
              <Play size={13} /> Preview schema
            </Btn>
            {s.connectorTests[c.id] && (
              <pre>
                {JSON.stringify(
                  {
                    instrument_id: c.id,
                    sample_id: s.samples[0].id,
                    timestamp: "2026-10-13T16:12:00Z",
                    method: c.method,
                    result: {
                      value: 338.2,
                      unit: "example unit — confirm device mapping",
                    },
                    source: "synthetic schema example",
                  },
                  null,
                  2,
                )}
              </pre>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

export function EnterpriseConnections({ s, mutate }: Props) {
  const [selected, setSelected] = useState("datacor");
  const [scope, setScope] = useState<string[]>([]);
  const [purpose, setPurpose] = useState(
    "Match customer orders and inventory to the lab work queue.",
  );
  const [role, setRole] = useState("Lab Manager");
  const [preview, setPreview] = useState(false);
  const c = enterpriseSystems.find((x) => x.id === selected)!;
  const grant = s.ops.grants[selected];
  return (
    <>
      <div className="compact-banner">
        <div>
          <strong>Enterprise connections · read-only by design</strong>
          <span>
            Permission is requested for specific datasets. No ERP writes, order
            submission, Teams posting or user impersonation.
          </span>
        </div>
        <Tag tone="success">
          <LockKeyhole size={11} /> READ ONLY
        </Tag>
      </div>
      <div className="directory-layout">
        <Card title="ERP, collaboration and laboratory sources">
          <div className="enterprise-grid">
            {enterpriseSystems.map((x) => (
              <button
                key={x.id}
                className={selected === x.id ? "selected" : ""}
                onClick={() => {
                  setSelected(x.id);
                  setScope(s.ops.grants[x.id]?.scopes || []);
                  setPreview(false);
                }}
              >
                <strong>{x.name}</strong>
                <small>{x.category}</small>
                <Tag>{s.ops.grants[x.id]?.status || "Not authorized"}</Tag>
              </button>
            ))}
          </div>
        </Card>
        <Card title={c.name + " · permission request"}>
          <div className="panel-body">
            <p>{c.adapter}</p>
            <label className="form-field">
              Role
              <select
                aria-label="Connection permission persona"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option>Lab Manager</option>
                <option>IT Administrator</option>
              </select>
            </label>
            <strong className="small-heading">Requested read scope</strong>
            {c.objects.map((o) => (
              <label className="scope-check" key={o}>
                <input
                  type="checkbox"
                  disabled={grant?.status === "Granted"}
                  checked={scope.includes(o)}
                  onChange={(e) =>
                    setScope(
                      e.target.checked
                        ? [...scope, o]
                        : scope.filter((x) => x !== o),
                    )
                  }
                />
                {o}
              </label>
            ))}
            <label className="form-field">
              Business purpose
              <textarea
                rows={2}
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              />
            </label>
            <div className="row wrap">
              <Btn
                disabled={
                  !scope.length ||
                  !purpose.trim() ||
                  role !== "Lab Manager" ||
                  grant?.status === "Granted"
                }
                onClick={() =>
                  mutate((d) => {
                    d.ops.grants[c.id] = {
                      status: "Requested",
                      scopes: scope,
                      reason: purpose,
                      requestedBy: "Maya Chen",
                      history: ["Maya Chen requested " + scope.join(", ")],
                    };
                    log(d, "Read permission requested", c.id, purpose);
                  }, "Permission request queued for IT administrator.")
                }
              >
                Request access
              </Btn>
              <Btn
                primary
                disabled={
                  role !== "IT Administrator" || grant?.status !== "Requested"
                }
                onClick={() =>
                  mutate((d) => {
                    d.ops.grants[c.id].status = "Granted";
                    d.ops.grants[c.id].approvedBy = "IT Administrator · demo";
                    d.ops.grants[c.id].history.push(
                      "IT Administrator granted selected read scopes",
                    );
                    log(
                      d,
                      "Demo read permission granted",
                      c.id,
                      d.ops.grants[c.id].scopes.join(", "),
                      "IT Administrator",
                    );
                  }, "Demo permission recorded. No live OAuth connection created.")
                }
              >
                Grant read access
              </Btn>
              {grant?.status === "Granted" && (
                <>
                  <Btn onClick={() => setPreview(true)}>
                    Preview permitted records
                  </Btn>
                  <Btn
                    disabled={role !== "IT Administrator"}
                    onClick={() => {
                      mutate((d) => {
                        d.ops.grants[c.id].status = "Revoked";
                        d.ops.grants[c.id].history.push(
                          "IT Administrator revoked access",
                        );
                        log(
                          d,
                          "Read access revoked",
                          c.id,
                          "Preview access removed",
                          "IT Administrator",
                        );
                      }, "Access revoked.");
                      setPreview(false);
                    }}
                  >
                    Revoke
                  </Btn>
                </>
              )}
            </div>
            {grant && (
              <p className="note">
                Requested by {grant.requestedBy} · {grant.status}.{" "}
                {grant.scopes.join(" · ")}
              </p>
            )}
            {preview && grant?.status === "Granted" && (
              <pre>
                {JSON.stringify(
                  {
                    source: c.name,
                    access: "read-only",
                    scopes: grant.scopes,
                    rows: grant.scopes.map((object, i) => ({
                      object,
                      record_id: "DEMO-" + (i + 1),
                      value: "Synthetic authorized example",
                    })),
                  },
                  null,
                  2,
                )}
              </pre>
            )}
            <p className="note">
              Catalog and permission workflow are demonstrable. Vendor-specific
              authentication and production connectors require implementation
              and validation; universal ERP coverage is not claimed.
            </p>
          </div>
        </Card>
      </div>
    </>
  );
}

export function PartsCatalog({ s, mutate, go }: Props) {
  const [search,setSearch]=useState("");
  const [asset,setAsset]=useState("All instruments");
  return (
    <>
      <div className="compact-banner">
        <div>
          <strong>Parts & materials catalog</strong>
          <span>
            Request → procurement details → lab director approval → purchase
            order → receipt.
          </span>
        </div>
        <Btn onClick={() => go("orders")}>
          Open ordering & approvals <ArrowRight size={14} />
        </Btn>
      </div>
      <Card title="Instrument spares and laboratory supplies">
        <div className="catalog-filters"><input aria-label="Search parts" placeholder="Search part, SKU or supplier" value={search} onChange={e=>setSearch(e.target.value)}/><select aria-label="Parts instrument" value={asset} onChange={e=>setAsset(e.target.value)}>{["All instruments",...new Set(parts.map(p=>p.instrument))].map(x=><option key={x}>{x}</option>)}</select><span>{parts.length} catalog items</span></div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Part / catalog reference</th>
                <th>Instrument</th>
                <th>Unit price</th>
                <th>Availability / lead time</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {parts.filter(p=>(asset==="All instruments"||p.instrument===asset)&&(p.name+" "+p.sku+" "+p.vendor).toLowerCase().includes(search.toLowerCase())).map((p) => (
                <tr key={p.id}>
                  <td>
                    <img className="part-thumbnail" src={"/parts/"+(p.instrument.startsWith("DIST")?"flask":p.instrument.startsWith("ELEM")?"cylinder":"supplies")+".svg"} alt="Illustrative catalog image"/><strong>{p.name}</strong>
                    <small>
                      {p.sku} · {p.vendor}
                    </small>
                  </td>
                  <td>{p.instrument}</td>
                  <td>${p.price}</td>
                  <td>
                    {p.stock}
                    <small>{p.lead}</small>
                  </td>
                  <td>
                    <Btn
                      onClick={() => {
                        mutate((d) => {
                          const id =
                            "REQ-" + String(d.ops.purchases.length + 101);
                          d.ops.purchases.push({
                            id,
                            partId: p.id,
                            qty: 1,
                            reason:
                              p.id === "P-104"
                                ? "Restore two-cylinder overnight reserve for ELEM-01."
                                : "Support " +
                                  p.instrument +
                                  " scheduled work.",
                            costCenter: "LAB-HOU-OPS",
                            delivery: "Receiving bay · Houston lab",
                            specification: "",
                            status: "Draft",
                            clarification: "",
                            history: [],
                          });
                          log(d, "Purchase request drafted", id, p.name);
                        }, "Draft request created; director approval is required.");
                        go("orders");
                      }}
                    >
                      Request
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="note">
        Demonstration catalog: prices, stock and SKUs are illustrative. No
        vendor order is transmitted.
      </p>
      <Card title="Knowledge subscriptions · existing Trucision content packs">
        <div className="content-packs">
          {packs.map((p) => (
            <div key={p.id}>
              <BookTitle title={p.title} text={p.detail} />
              <Btn
                onClick={() =>
                  s.packs.includes(p.id)
                    ? go("wiki")
                    : mutate((d) => {
                        d.packs.push(p.id);
                        log(d, "Content pack installed", p.id, p.title);
                      }, "Content added to Knowledge Hub.")
                }
              >
                {s.packs.includes(p.id)
                  ? "Open installed content"
                  : "Subscribe to pack"}
              </Btn>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
function BookTitle({ title, text }: { title: string; text: string }) {
  return (
    <div>
      <strong>{title}</strong>
      <small>{text}</small>
    </div>
  );
}

export function Ordering({ s, mutate, go }: Props) {
  const [role, setRole] = useState("Lab Manager");
  const [selected, setSelected] = useState(s.ops.purchases[0]?.id || "");
  const [error, setError] = useState("");
  const [note, setNote] = useState(
    "Please confirm purity grade, cylinder size and compatible regulator.",
  );
  const p =
    s.ops.purchases.find((x) => x.id === selected) || s.ops.purchases[0];
  const part = parts.find((x) => x.id === p?.partId);
  function step(action: Parameters<typeof purchaseStep>[1]) {
    try {
      const next = purchaseStep(p, action, role, note);
      mutate((d) => {
        d.ops.purchases = d.ops.purchases.map((x) =>
          x.id === p.id ? next : x,
        );
        log(
          d,
          "Purchase " + action,
          p.id,
          part!.name + " · " + next.status,
          role,
        );
      }, "Request updated.");
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function field(key: keyof Purchase, value: string | number) {
    mutate((d) => {
      const x = d.ops.purchases.find((x) => x.id === p.id)!;
      Object.assign(x, { [key]: value });
    }, "Draft updated.");
  }
  const editable =
    p &&
    ["Draft", "Needs details"].includes(p.status) &&
    role === "Lab Manager";
  return (
    <>
      <div className="compact-banner">
        <div>
          <strong>Ordering & approval queue</strong>
          <span>
            Only the Lab Director approves spend. Procurement may return a
            request for missing details.
          </span>
        </div>
        <label className="row">
          Role
          <select
            aria-label="Purchase persona"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option>Lab Manager</option>
            <option>Lab Director</option>
            <option>Procurement</option>
          </select>
        </label>
      </div>
      <div className="directory-layout">
        <Card
          title="Requests"
          action={
            <button className="text-link" onClick={() => go("marketplace")}>
              Add request →
            </button>
          }
        >
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Request / item</th>
                  <th>Value</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {s.ops.purchases.map((x) => (
                  <tr
                    key={x.id}
                    className={p?.id === x.id ? "selected-row" : ""}
                  >
                    <td>
                      <button
                        className="text-link"
                        onClick={() => setSelected(x.id)}
                      >
                        {x.id}
                      </button>
                      <small>
                        {parts.find((a) => a.id === x.partId)?.name}
                      </small>
                    </td>
                    <td>
                      $
                      {x.qty *
                        (parts.find((a) => a.id === x.partId)?.price || 0)}
                    </td>
                    <td>
                      <Tag>{x.status}</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!p && (
              <p className="empty">
                No requests yet. Choose an item in Parts & materials.
              </p>
            )}
          </div>
        </Card>
        {p && (
          <Card title={p.id + " · " + part?.name}>
            <div className="panel-body">
              <div className="order-fields">
                <label className="form-field">
                  Quantity
                  <input
                    aria-label="Purchase quantity"
                    type="number"
                    min="1"
                    disabled={!editable}
                    value={p.qty}
                    onChange={(e) =>
                      field("qty", Math.max(1, Number(e.target.value)))
                    }
                  />
                </label>
                <label className="form-field">
                  Cost center
                  <input
                    aria-label="Cost center"
                    disabled={!editable}
                    value={p.costCenter}
                    onChange={(e) => field("costCenter", e.target.value)}
                  />
                </label>
              </div>
              <label className="form-field">
                Justification
                <textarea
                  aria-label="Purchase justification"
                  disabled={!editable}
                  value={p.reason}
                  onChange={(e) => field("reason", e.target.value)}
                  rows={2}
                />
              </label>
              <label className="form-field">
                Technical specification
                <input
                  aria-label="Technical specification"
                  placeholder="Grade, size, compatibility or OEM part number"
                  disabled={!editable}
                  value={p.specification}
                  onChange={(e) => field("specification", e.target.value)}
                />
              </label>
              <label className="form-field">
                Ship-to / required date
                <input
                  aria-label="Delivery details"
                  disabled={!editable}
                  value={p.delivery}
                  onChange={(e) => field("delivery", e.target.value)}
                />
              </label>
              {p.clarification && (
                <p className="note">Procurement asks: {p.clarification}</p>
              )}
              {role === "Procurement" && p.status === "Awaiting director" && (
                <label className="form-field">
                  Clarification request
                  <input
                    aria-label="Clarification request"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
              )}
              <div className="row wrap">
                {p.status === "Draft" && (
                  <Btn
                    primary
                    disabled={role !== "Lab Manager"}
                    onClick={() => step("submit")}
                  >
                    Submit for approval
                  </Btn>
                )}
                {p.status === "Needs details" && (
                  <Btn
                    primary
                    disabled={role !== "Lab Manager"}
                    onClick={() => step("resubmit")}
                  >
                    Resubmit details
                  </Btn>
                )}
                {p.status === "Awaiting director" && (
                  <>
                    <Btn
                      primary
                      disabled={role !== "Lab Director"}
                      onClick={() => step("approve")}
                    >
                      Approve purchase
                    </Btn>
                    <Btn
                      disabled={role !== "Procurement"}
                      onClick={() => step("clarify")}
                    >
                      Request more details
                    </Btn>
                  </>
                )}
                {p.status === "Approved" && (
                  <Btn
                    primary
                    disabled={role !== "Procurement"}
                    onClick={() => step("order")}
                  >
                    Create demo purchase order
                  </Btn>
                )}
                {p.status === "Ordered" && (
                  <Btn
                    primary
                    disabled={role !== "Procurement"}
                    onClick={() => step("receive")}
                  >
                    Record goods received
                  </Btn>
                )}
              </div>
              {error && (
                <p role="alert" className="note error-note">
                  {error}
                </p>
              )}
              {p.approvedBy && (
                <p className="note">
                  Spend approved by {p.approvedBy}. Approved details are frozen;
                  changes need a new request.
                </p>
              )}
              <ul className="timeline">
                {p.history.map((h, i) => (
                  <li key={i}>
                    {h.actor} · {h.action}
                  </li>
                ))}
              </ul>
              {p.status === "Received" && (
                <Btn onClick={() => go("schedule")}>
                  Verify updated materials readiness
                </Btn>
              )}
            </div>
          </Card>
        )}
      </div>
      <p className="note">
        Role switching is an explicit demo control, not real identity
        verification. Purchase orders remain local; no supplier is contacted.
      </p>
    </>
  );
}

export function AuditBinder({ s }: Pick<Props, "s">) {
  const checks = evidenceChecklist(s);
  const records = recordIndex(s);
  const [selected, setSelected] = useState("CAL-184");
  const [error, setError] = useState("");
  const r = records.find((x) => x.id === selected);
  return (
    <>
      <div className="compact-banner">
        <div>
          <strong>NS-1042 · source-record evidence dossier</strong>
          <span>
            {checks.filter((c) => c.complete).length}/{checks.length} evidence
            groups available · Coverage is not a compliance score.
          </span>
        </div>
        <Btn
          primary
          onClick={() => {
            evidenceEnvelope(s)
              .then((e) =>
                download(
                  "Trucision-NS-1042-evidence.json",
                  JSON.stringify(e, null, 2),
                ),
              )
              .catch((e) => setError(e.message));
          }}
        >
          Download evidence package
        </Btn>
      </div>
      {error && <p role="alert">{error}</p>}
      <div className="audit-layout">
        <Card title="Evidence index · ISO/IEC 17025:2017 topics">
          <div className="audit-checks">
            {checks.map((c) => (
              <div key={c.id}>
                <div className="row between">
                  <strong>{c.title}</strong>
                  <Tag tone={c.complete ? "success" : "amber"}>
                    {c.complete ? "Records available" : "Pending"}
                  </Tag>
                </div>
                <small>
                  §{c.clause} · {c.review}
                </small>
                <div className="record-links">
                  {c.refs.map((id) => (
                    <button
                      key={id}
                      disabled={!records.some((r) => r.id === id)}
                      className="source-chip"
                      onClick={() => setSelected(id)}
                    >
                      {id}
                      {!records.some((r) => r.id === id) ? " · missing" : ""}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card
          title={r?.title || "Select a source record"}
          action={r && <Tag>{r.id}</Tag>}
        >
          <div className="panel-body">
            {r && (
              <>
                <div className="record-metadata">
                  <span>
                    Origin<strong>{r.source}</strong>
                  </span>
                  <span>
                    Recorded by<strong>{r.actor}</strong>
                  </span>
                  <span>
                    Version<strong>{r.revision}</strong>
                  </span>
                  <span>
                    Recorded at<strong>{r.at}</strong>
                  </span>
                </div>
                <RecordData data={r.data} />
                <h3>Linked event records</h3>
                {r.eventIds.length ? (
                  r.eventIds.map((id) => {
                    const e = s.audit.find((e) => e.id === id);
                    return (
                      <div className="record-event" key={id}>
                        <strong>
                          {id} · {e?.action}
                        </strong>
                        <small>
                          {e?.at} · {e?.actor}
                        </small>
                        <p>{e?.detail}</p>
                      </div>
                    );
                  })
                ) : (
                  <p className="note">
                    Baseline synthetic fixture. This is not a scanned supplier
                    certificate or externally verified record.
                  </p>
                )}
                <Btn
                  onClick={() =>
                    download(r.id + ".json", JSON.stringify(r, null, 2))
                  }
                >
                  Download this record
                </Btn>
              </>
            )}
          </div>
        </Card>
      </div>
      <Card title="Record integrity and review limitations">
        <div className="panel-body">
          <div className="two-grid">
            <div>
              <strong>What the package retains</strong>
              <p>
                Original and retest values, sample/run identifiers, procedure
                versions, actors, approvals and event IDs. Each record includes
                a SHA-256 checksum of its exported JSON.
              </p>
            </div>
            <div>
              <strong>What is still a production requirement</strong>
              <p>
                Validated systems, authenticated signatures, retention controls,
                genuine calibration certificates and method-specific report
                review. Local hashes do not prove authenticity.
              </p>
            </div>
          </div>
          <p className="note">
            Report COA-1042 is a T90 excerpt. It does not claim full ASTM D86
            reporting or product conformity. “Authorized” does not mean
            delivered: the export ledger records browser downloads only.
          </p>
        </div>
      </Card>
      <Card title="Chronological action trail">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Timestamp · UTC</th>
                <th>Actor</th>
                <th>Record / action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {s.audit.map((e) => (
                <tr key={e.id}>
                  <td className="mono">{e.at}</td>
                  <td>{e.actor}</td>
                  <td>
                    {e.record}
                    <small>
                      {e.id} · {e.action}
                    </small>
                  </td>
                  <td>{e.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
function RecordData({ data }: { data: unknown }) {
  if (!data || typeof data !== "object")
    return <p>{String(data ?? "Not recorded")}</p>;
  return (
    <div className="record-data">
      {Object.entries(data as Record<string, unknown>).map(([key, value]) => (
        <div key={key}>
          <small>{key.replace(/([A-Z])/g, " $1").replaceAll("_", " ")}</small>
          {value !== null && typeof value === "object" ? (
            <details>
              <summary>
                {Array.isArray(value)
                  ? value.length + " entries"
                  : "View structured record"}
              </summary>
              <pre>{JSON.stringify(value, null, 2)}</pre>
            </details>
          ) : (
            <strong>{String(value ?? "Not recorded")}</strong>
          )}
        </div>
      ))}
    </div>
  );
}

const knowledgeSeeds = [
  {
    id: "KH-INST-01",
    title: "OptiDist operator manual",
    category: "User Manual",
    type: "Instrument",
    instrument: "DIST-04",
    revision: 3,
    body: "Synthetic manual excerpt: verify flask condition and seating, reference thermometer check, sample ID and run method before starting. Use the actual vendor manual for equipment instructions.",
  },
  {
    id: "KH-GOV-01",
    title: "Result authorization and exceptions",
    category: "Others",
    type: "Governance",
    instrument: "All assets",
    revision: 2,
    body: "Only the lab manager authorizes report release. A method deviation requires a documented exception, impact review, customer agreement where applicable, and approval before release.",
  },
  {
    id: "KH-WF-01",
    title: "Nonconforming-work response",
    category: "Troubleshooting",
    type: "Workflow",
    instrument: "All assets",
    revision: 4,
    body: "Identify the affected interval; hold results; investigate and retain evidence; independently review cause; approve corrective work; verify recovery and reassess prior work.",
  },
  {
    id: "KH-APR-01",
    title: "Purchase approval matrix",
    category: "Others",
    type: "Approvals",
    instrument: "All assets",
    revision: 1,
    body: "Lab manager drafts requests. Procurement checks specification and delivery details. Lab Director approves spend. Procurement creates the purchase order only after approval.",
  },
  {
    id: "KH-EXC-01",
    title: "EXC-014 · alternate-instrument retest",
    category: "Others",
    type: "Exceptions",
    instrument: "DIST-02",
    revision: 1,
    body: "Retained Northstar aliquots may be retested on DIST-02 after valid QC. Original DIST-04 results remain retained. Maya Chen authorizes release after review; the exception does not waive QC.",
  },
  {
    id: "KH-SAFE-01",
    title: "Flammable sample handling",
    category: "Safety Guidelines",
    type: "Safety",
    instrument: "All assets",
    revision: 2,
    body: "Synthetic briefing record. Use approved flammable storage, labeled retained aliquots and the current laboratory safety procedure.",
  },
];
export function KnowledgeHub({ s, mutate }: Props) {
  const [filter, setFilter] = useState("All sources");
  const [q, setQ] = useState("");
  const [id, setId] = useState("CTX-01");
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const docs = [
    ...s.wiki.map((w) => ({
      id: w.id,
      title: w.title,
      body: w.text,
      revision: w.rev,
      type:
        w.id === "CTX-03"
          ? "SOPs"
          : w.id === "CTX-02"
            ? "Semantic layer"
            : "Governance",
      category: w.id === "CTX-03" ? "SOP" : "Others",
      instrument: w.id === "CTX-03" ? "DIST-04" : "All assets",
    })),
    ...knowledgeSeeds,
    ...s.ops.knowledgeNotes.map((w) => ({
      ...w,
      type: w.category,
      instrument: "All assets",
    })),
    ...s.packs.map((pid) => {
      const p = packs.find((x) => x.id === pid)!;
      return {
        id: pid,
        title: p.title,
        body: p.detail,
        revision: 1,
        type: "Marketplace",
        category: "Technical Know-hows",
        instrument: "All assets",
      };
    }),
  ];
  const d = docs.find((x) => x.id === id) || docs[0];
  const active = docs.filter(
    (x) =>
      (filter === "All sources" || x.type === filter) &&
      (x.title + " " + x.body + " " + x.instrument)
        .toLowerCase()
        .includes(q.toLowerCase()),
  );
  return (
    <>
      <BenchTips key={s.lab} s={s} mutate={mutate} />
      <div className="compact-banner">
        <div>
          <strong>Knowledge Hub · {s.lab}</strong>
          <span>
            Instrument knowledge, controlled SOPs, governance, workflows,
            approvals and exceptions.
          </span>
        </div>
        <Btn
          onClick={() => {
            const next = "KH-LOCAL-" + (s.ops.knowledgeNotes.length + 1);
            mutate((v) => {
              v.ops.knowledgeNotes.push({
                id: next,
                title: "New knowledge record",
                category: "Governance",
                body: "Describe the source, owner and approved operating context.",
                revision: 1,
              });
              log(
                v,
                "Knowledge record created",
                next,
                "Local controlled context",
              );
            }, "Knowledge record created.");
            setId(next);
            setTitle("New knowledge record");
            setBody(
              "Describe the source, owner and approved operating context.",
            );
            setEditing(true);
          }}
        >
          Add knowledge
        </Btn>
      </div>
      <div className="toolbar">
        <label className="search">
          <Search size={14} />
          <input
            aria-label="Search knowledge"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search source, instrument or procedure"
          />
        </label>
        <select
          aria-label="Knowledge source type"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {[
            "All sources",
            "Instrument",
            "SOPs",
            "Governance",
            "Workflow",
            "Approvals",
            "Exceptions",
            "Safety",
            "Semantic layer",
            "Marketplace",
          ].map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
      </div>
      <div className="directory-layout">
        <Card title="Sources" action={<Tag>{active.length} records</Tag>}>
          <div className="knowledge-list">
            {active.map((x) => (
              <button
                className={d.id === x.id ? "selected" : ""}
                key={x.id}
                onClick={() => {
                  setId(x.id);
                  setEditing(false);
                }}
              >
                <FileText size={16} />
                <div>
                  <strong>{x.title}</strong>
                  <small>
                    {x.id} · {x.type} · rev {x.revision} · {x.instrument}
                  </small>
                </div>
              </button>
            ))}
          </div>
          <div className="panel-body">
            <small>
              Imported Trucision source categories:{" "}
              {KNOWLEDGE_HUB_CATEGORIES.join(" · ")}
            </small>
          </div>
        </Card>
        <Card title={d.title} action={<Tag>Revision {d.revision}</Tag>}>
          <div className="panel-body">
            <div className="row">
              <Tag>{d.type}</Tag>
              <Tag>{d.category}</Tag>
              <Tag>{d.instrument}</Tag>
            </div>
            {editing ? (
              <>
                <label className="form-field section-gap">
                  Title
                  <input
                    aria-label="Context title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </label>
                <label className="form-field">
                  Content
                  <textarea
                    aria-label="Context content"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={7}
                  />
                </label>
                <div className="row">
                  <Btn
                    primary
                    disabled={!title.trim() || !body.trim()}
                    onClick={() => {
                      mutate((v) => {
                        const w = v.wiki.find((x) => x.id === d.id);
                        const n = v.ops.knowledgeNotes.find(
                          (x) => x.id === d.id,
                        );
                        log(
                          v,
                          "Knowledge revised",
                          d.id,
                          JSON.stringify({
                            previousTitle: d.title,
                            previousBody: d.body,
                            previousRevision: d.revision,
                          }),
                        );
                        if (w) {
                          w.title = title;
                          w.text = body;
                          w.rev++;
                        } else if (n) {
                          n.title = title;
                          n.body = body;
                          n.revision++;
                        }
                      }, "Revision retained in lab history.");
                      setEditing(false);
                    }}
                  >
                    Save revision
                  </Btn>
                  <Btn onClick={() => setEditing(false)}>Cancel</Btn>
                </div>
              </>
            ) : (
              <>
                <p className="knowledge-body">{d.body}</p>
                <Btn
                  disabled={
                    !s.wiki.some((w) => w.id === d.id) &&
                    !s.ops.knowledgeNotes.some((w) => w.id === d.id)
                  }
                  onClick={() => {
                    setTitle(d.title);
                    setBody(d.body);
                    setEditing(true);
                  }}
                >
                  Edit context
                </Btn>
              </>
            )}
            <div className="semantic section-gap">
              <span>Instrument</span>
              <ArrowRight size={13} />
              <span>Method / SOP</span>
              <ArrowRight size={13} />
              <span>Approval rule</span>
              <ArrowRight size={13} />
              <span>Evidence</span>
            </div>
            <p className="note">
              Source model and categories are adapted from the existing
              Trucision Knowledge Hub. Local demo edits preserve prior content
              in the activity trail; external document ingestion is not
              connected.
            </p>
            <details>
              <summary>Activity for this record</summary>
              {s.audit
                .filter((e) => e.record === d.id)
                .map((e) => (
                  <p key={e.id}>
                    {e.actor} · {e.action} · {e.detail}
                  </p>
                ))}
            </details>
          </div>
        </Card>
      </div>
    </>
  );
}
